/**
 * Client-side auth for Vite admin app. Communicates with the NestJS backend's
 * NextAuth endpoints via fetch (all /api/* requests are proxied to the
 * backend in dev via Vite proxy).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

export type SessionUser = {
  id: string;
  email: string | null;
  name: string | null;
  image: string | null;
  role: string | null;
  /** Set by NextAuth session callback — mirrors DB ADMIN role + ADMIN_EMAILS. */
  adminAccess?: boolean;
};

export type Session = {
  user: SessionUser;
  expires: string;
} | null;

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  session: Session;
  status: AuthStatus;
  refresh: () => Promise<void>;
  signOut: (options?: { callbackUrl?: string }) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  session: null,
  status: "loading",
  refresh: async () => {},
  signOut: async () => {},
});

function resolvePostAuthRedirectUrl(callbackUrl?: string): string {
  if (
    callbackUrl &&
    callbackUrl.startsWith("/") &&
    !callbackUrl.startsWith("//")
  ) {
    return callbackUrl;
  }
  return "/admin/signin";
}

async function fetchSession(): Promise<Session> {
  try {
    const res = await fetch("/api/auth/session", {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Session;
    if (!data || !("user" in data)) return null;
    return data;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [session, setSession] = useState<Session>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const refresh = useCallback(async () => {
    const s = await fetchSession();
    setSession(s);
    setStatus(s ? "authenticated" : "unauthenticated");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, location.pathname, location.search]);

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    }
    function onPageShow(ev: PageTransitionEvent) {
      if (ev.persisted) {
        void refresh();
      }
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [refresh]);

  const signOut = useCallback(
    async (options?: { callbackUrl?: string }) => {
      const nextUrl = resolvePostAuthRedirectUrl(options?.callbackUrl);
      try {
        const csrfRes = await fetch("/api/auth/csrf", {
          credentials: "include",
        });
        const { csrfToken } = (await csrfRes.json()) as {
          csrfToken?: string;
        };

        await fetch("/api/auth/signout", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            csrfToken: csrfToken ?? "",
            callbackUrl: nextUrl,
            json: "true",
          }),
        });
      } catch {
        // Ignore errors
      }
      setSession(null);
      setStatus("unauthenticated");
      window.location.href = nextUrl;
    },
    [],
  );

  return (
    <AuthContext.Provider value={{ session, status, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useSession() {
  const { session, status } = useAuth();
  return { data: session, status };
}

export function isAdmin(session: Session): boolean {
  if (!session?.user) return false;
  if (session.user.adminAccess === true) return true;
  return session.user.role === "ADMIN";
}
